import { apiClient } from './client';

/**
 * Client for `/api/admin/reports/shipedge/*` (Phase 15 Day 6/7).
 * Stays in lockstep with `server/src/routes/admin/reports/shipedge.routes.ts`.
 *
 * All cost / percent fields are 2-decimal strings — Decimal-safe by
 * construction (the server aggregates in Postgres on `Decimal(10,2)`
 * columns; converting to JS numbers for display is a parseFloat at
 * the render boundary).
 */

export type ShipedgePickType = 'batch' | 'wholesale' | 'distributor' | 'internal';
export type ShipedgeOrderType = 'fulfillment' | 'private_label';
export type ShipedgeReportEndpoint = 'orders' | 'line_items';
export type ShipedgeSyncTrigger = 'cron' | 'admin_manual' | 'backfill';
export type ShipedgeSyncStatus = 'pending' | 'success' | 'failed';

// ─── Customer Profitability ─────────────────────────────────────────────

export interface CustomerProfitabilityRow {
  client: string;
  clientNormalizedKey: string;
  shipmentCount: number;
  unitsShipped: number;
  markupRevenue: string;
  handlingFeeRevenue: string;
  boxCostBurden: string;
  totalBilled: string;
}

export interface CustomerProfitabilityQuery {
  /** YYYY-MM */
  month: string;
  pickTypes?: ShipedgePickType[];
  orderTypes?: ShipedgeOrderType[];
}

export async function getCustomerProfitability(
  q: CustomerProfitabilityQuery,
): Promise<CustomerProfitabilityRow[]> {
  const params = new URLSearchParams({ month: q.month });
  if (q.pickTypes?.length) params.set('pickTypes', q.pickTypes.join(','));
  if (q.orderTypes?.length) params.set('orderTypes', q.orderTypes.join(','));
  const res = await apiClient.get<{ data: CustomerProfitabilityRow[] }>(
    `/api/admin/reports/shipedge/customer-profitability?${params.toString()}`,
  );
  return res.data.data;
}

// ─── Operational Mix ────────────────────────────────────────────────────

export interface PickTypeBreakdownEntry {
  pickType: ShipedgePickType | null;
  count: number;
  percent: string;
}

export interface CarrierMixEntry {
  carrier: string;
  count: number;
  percent: string;
}

export interface OperationalMixResult {
  startDate: string;
  endDate: string;
  totalShipments: number;
  pickTypeBreakdown: PickTypeBreakdownEntry[];
  carrierMix: CarrierMixEntry[];
  internalOpsPercent: string;
}

export interface OperationalMixQuery {
  startDate: string;
  endDate: string;
  topCarriers?: number;
}

export async function getOperationalMix(q: OperationalMixQuery): Promise<OperationalMixResult> {
  const params = new URLSearchParams({ startDate: q.startDate, endDate: q.endDate });
  if (q.topCarriers !== undefined) params.set('topCarriers', String(q.topCarriers));
  const res = await apiClient.get<{ data: OperationalMixResult }>(
    `/api/admin/reports/shipedge/operational-mix?${params.toString()}`,
  );
  return res.data.data;
}

// ─── Lot Lookup ─────────────────────────────────────────────────────────

export interface LotLookupRow {
  lineItemId: string;
  shippingNumber: string;
  orderNumber: string;
  sku: string;
  skuDescription: string | null;
  unitQty: number;
  skuLot: string | null;
  /** Earliest expiration for " / "-merged multi-lot rows. */
  skuExpiration: string | null;
  /** Latest expiration across merged lots; equals skuExpiration for
   *  unmerged rows. Render a range when the two differ. */
  skuExpirationLatest: string | null;
  shippedAt: string;
  client: string;
  carrier: string | null;
  pickType: ShipedgePickType | null;
  orderType: ShipedgeOrderType;
  totalCost: string | null;
}

export interface LotLookupPage {
  rows: LotLookupRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface LotLookupQuery {
  sku?: string;
  skuLot?: string;
  skuExpirationAfter?: string;
  skuExpirationBefore?: string;
  client?: string;
  limit?: number;
  offset?: number;
}

export async function lookupLotLineItems(q: LotLookupQuery): Promise<LotLookupPage> {
  const params = new URLSearchParams();
  if (q.sku) params.set('sku', q.sku);
  if (q.skuLot) params.set('skuLot', q.skuLot);
  if (q.skuExpirationAfter) params.set('skuExpirationAfter', q.skuExpirationAfter);
  if (q.skuExpirationBefore) params.set('skuExpirationBefore', q.skuExpirationBefore);
  if (q.client) params.set('client', q.client);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  const qs = params.toString();
  const res = await apiClient.get<{ data: LotLookupPage }>(
    `/api/admin/reports/shipedge/lot-lookup${qs ? `?${qs}` : ''}`,
  );
  return res.data.data;
}

// ─── Sync log + manual sync ─────────────────────────────────────────────

export interface ShipedgeReportSyncLogRow {
  id: string;
  endpoint: ShipedgeReportEndpoint;
  startDate: string;
  endDate: string;
  triggeredBy: ShipedgeSyncTrigger;
  triggeredByMembershipId: string | null;
  status: ShipedgeSyncStatus;
  rowsIngested: number;
  durationMs: number | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
}

export async function listSyncLog(limit = 25): Promise<ShipedgeReportSyncLogRow[]> {
  const res = await apiClient.get<{ data: ShipedgeReportSyncLogRow[] }>(
    `/api/admin/reports/shipedge/sync-log?limit=${String(limit)}`,
  );
  return res.data.data;
}

export interface RunSyncRequest {
  startDate: string;
  endDate: string;
}

export interface EndpointSyncResultDto {
  endpoint: ShipedgeReportEndpoint;
  status: 'success' | 'failed';
  rowsIngested: number;
  parseErrors: number;
  durationMs: number;
  error: string | null;
  syncLogId: string;
}

export interface RunSyncResult {
  organizationId: string;
  range: { startDate: string; endDate: string };
  orders: EndpointSyncResultDto;
  lineItems: EndpointSyncResultDto;
}

export async function runShipedgeReportsSync(body: RunSyncRequest): Promise<RunSyncResult> {
  const res = await apiClient.post<{ data: RunSyncResult }>('/api/admin/reports/shipedge/sync', body);
  return res.data.data;
}

// ─── Availability ───────────────────────────────────────────────────────

/**
 * Whether the (org-gated, fulfillment-specific) report surface is enabled
 * for the caller's org. The generic Insights dashboard uses this to decide
 * whether to show a discreet cross-link to these reports — every non-
 * allowlisted tenant gets `false` and never sees the fulfillment surface.
 */
export async function getShipedgeReportsStatus(): Promise<{ enabled: boolean }> {
  const res = await apiClient.get<{ data: { enabled: boolean } }>('/api/admin/reports/shipedge/status');
  return res.data.data;
}

// ─── Constants for UI ───────────────────────────────────────────────────

export const PICK_TYPE_LABELS: Record<ShipedgePickType, string> = {
  batch: 'DTC (Batch)',
  wholesale: 'Wholesale',
  distributor: 'Distributor',
  internal: 'Internal',
};

export const ORDER_TYPE_LABELS: Record<ShipedgeOrderType, string> = {
  fulfillment: 'Fulfillment',
  private_label: 'Private Label',
};
